import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button } from '@mui/material'
// El icono de DOCUMENTO de MIC, el mismo que ya usan `WidgetDocument` y la cabecera del job en
// MOS: el operario reconoce la ventana por su icono antes que por su rótulo, y una carpeta
// significaba otra cosa en el resto de la aplicación.
import DescriptionIcon from '@mui/icons-material/Description'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import useDocumentAuthorities from './useDocumentAuthorities'
import AlertUI from '../../../../generic/AlertUI'
import { hasAnyDocumentViewPermission } from './fabricationDocumentSources'
import FabricationDocumentsDialog from './FabricationDocumentsDialog'

// Historia 148 — EL acceso a la documentación de la fabricación, UNA sola pieza (RN29/S15).
//
// Los cuatro puntos de MOS —el widget de Operación, el detalle de la orden de fabricación, el
// detalle de trabajos activos y la vista de detalle del maestro-detalle— MONTAN este componente;
// ninguno copia su condición. Es la lección R-16 de la historia 120: tres copias de la misma regla
// divergen en la primera corrección.
//
// Entrada: exactamente un par de identificadores (RN2/R2). No admite «solo OF», no admite
// explorar, y no hay camino desde aquí a la documentación de otra fabricación.
//
// D18 fija qué significa «pulsable con guantes» de forma verificable: área táctil mínima de
// 48×48 px, texto + icono (nunca solo icono) y, SIN selección, el botón queda HABILITADO con aviso
// — no deshabilitado con tooltip, porque un tooltip no se ve al tocar con guantes (RN32/S2).
//
// `compact` es la EXCEPCIÓN a «nunca solo el icono» que S128 firma, y solo para el widget de
// Operación con dos columnas o menos, donde el rótulo entero empuja la cabecera a una segunda línea
// y le come sitio a la operación. Está acotada:
//   · solo cuando el punto de montaje lo pide —por defecto es falso, así que los otros tres puntos
//     siguen exactamente como S15 los fija—,
//   · el área táctil sigue siendo de 48×48 (RN30/D18),
//   · y el nombre accesible sigue siendo «Documents», vía `aria-label`: el botón no se vuelve mudo
//     para un lector de pantalla ni para las pruebas que lo buscan por su nombre.
const SHOP_FLOOR_BUTTON_SX = {
  minHeight: 48,
  minWidth: 48,
  textTransform: 'none',
  fontSize: '1.05rem',
  borderRadius: 2
}

/**
 * @param {Object} props
 * @param {string|number} props.fabricationOrderId Id de la orden de fabricación en contexto
 * @param {string|number} props.jobReferenceId `jobReference.id` del job en contexto — NUNCA el id
 * del nodo de workflow, que pertenece al ámbito documental temporal (corrección del as-is nº1, S13)
 * @param {boolean} props.fullWidth Si el punto de MOS lo quiere a ancho completo
 * @param {boolean} props.compact Solo el icono, para el widget estrecho (ver la nota de S15 arriba)
 * @returns {JSX.Element} El botón, o nada si el perfil no puede ver ninguna procedencia (RN19/S16)
 */
const FabricationDocumentsButton = ({
  fabricationOrderId = '',
  jobReferenceId = '',
  fullWidth = false,
  compact = false
}) => {
  const { t_ } = useSmartTranslation()
  // Lectura de authorities SIN los efectos de `useUser` (ver `useDocumentAuthorities`): este gate
  // se monta en las cuatro superficies de planta y no puede tumbarlas ni añadirles un reloj.
  const isUserAuthorized = useDocumentAuthorities()
  const [open, setOpen] = useState(false)
  const [AlertElement, showAlert] = AlertUI()

  // Los hooks van todos por encima del corte: un `return` temprano sobre ellos rompe el orden de
  // hooks y tumba la pantalla en tiempo de ejecución (incidente real en el maestro-detalle).
  if (!hasAnyDocumentViewPermission(isUserAuthorized)) {
    return null
  }

  const hasSelection = fabricationOrderId !== '' && fabricationOrderId !== undefined && fabricationOrderId !== null &&
    jobReferenceId !== '' && jobReferenceId !== undefined && jobReferenceId !== null

  const handleClick = () => {
    // RN32/S2: sin selección se AVISA. Nunca se abre un modal con una lista vacía, y no se emite
    // ninguna petición: el modal es quien consulta, y no llega a montarse abierto.
    if (!hasSelection) {
      showAlert({
        title: '',
        message: t_('Select a fabrication order and an operation first'),
        severity: 'warning'
      })
      return
    }
    setOpen(true)
  }

  return (
    <Box sx={fullWidth ? { width: '100%' } : undefined}>
      {AlertElement}
      {/* Un `Button` liso, NO `WorkerRequiredButton`: consultar documentación no es una acción
          operativa y no puede exigir operario presente (S12) ni cambiar su rótulo a «Worker
          required», que dejaría S2 y S15 sin botón que pulsar. */}
      <Button
        variant='outlined'
        color='primary'
        fullWidth={fullWidth}
        startIcon={compact ? undefined : <DescriptionIcon />}
        onClick={handleClick}
        // El nombre accesible NO depende de si hay rótulo visible: con `compact` el texto desaparece
        // de la pantalla, no del botón.
        aria-label={t_('Documents')}
        title={t_('Documents')}
        sx={compact ? { ...SHOP_FLOOR_BUTTON_SX, px: 1 } : SHOP_FLOOR_BUTTON_SX}
      >
        {compact ? <DescriptionIcon /> : t_('Documents')}
      </Button>
      <FabricationDocumentsDialog
        open={open}
        fabricationOrderId={fabricationOrderId}
        jobReferenceId={jobReferenceId}
        onClose={() => setOpen(false)}
      />
    </Box>
  )
}

FabricationDocumentsButton.propTypes = {
  fabricationOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobReferenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullWidth: PropTypes.bool,
  compact: PropTypes.bool
}

export { SHOP_FLOOR_BUTTON_SX }
export default FabricationDocumentsButton
